import React, { ChangeEvent, useEffect, useState } from "react";
import {
  AutomaticDownloadMode,
  Installation,
  Organization,
  Video,
  VideoCataloguesDto,
} from "../common/ObjectTypes";
import { toast } from "react-toastify";
import { MoonLoader } from "react-spinners";
import AddEditInstallation from "./AddEditInstallation";
import { useNavigate, useParams } from "react-router-dom";
import {
  createInstallation,
  deleteInstallation,
  getInstallationById,
  updateInstallation,
} from "../../api/installations/installationApi";
import {
  getVideoCatalogues,
  getVideosByCatalogueId,
} from "../../api/videoCatalogues.tsx/videoCataloguesApi";
import { getVideos } from "../../api/videos/videosApi";
import { getCurrentUser } from "../../helpers/authHeader";
import { getOrganizations } from "../../api/organizations/organizationsApi";
import { getAutomaticDownloadModes } from "../../api/automaticDownloadMode/automaticDownloadsApi";

type ManageAddEditInstallationProps = {
  existingInstallation: boolean;
};

const ManageAddEditInstallation = ({
  existingInstallation,
}: ManageAddEditInstallationProps) => {
  const { v4: uuidv4 } = require("uuid");
  const currentUser = getCurrentUser();
  const [pageLoaded, setPageLoaded] = useState(false);
  const [videoCatalogues, setVideoCatalogues] = useState<VideoCataloguesDto[]>(
    []
  );
  const [automaticDownloadModes, setAutomaticDownloadModes] = useState<
    AutomaticDownloadMode[]
  >([]);
  const [videos, setVideos] = useState<Video[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const navigate = useNavigate();
  const [addInstallation, setAddInstallation] = useState(false);
  const { id: installationId } = useParams<{ id: string }>(); // Extract the `id` parameter
  const [errors, setErrors] = useState<any>({});
  const [installation, setInstallation] = useState<Installation>({
    installationId: "",
    name: "",
    videoCatalogueId: "",
    forceOfflineMode: false,
    showGraphicDeath: false,
    showGraphicSex: false,
    controls: "Gaze",
    demoMode: true,
    globeStartLat: 0,
    globeStartLong: -10,
    appTimerLengthSeconds: 0,
    idleTimerLengthSeconds: 0,
    idleTimerDelaySeconds: 0,
    startupVideoId: "",
    resumeStartupVideoOnAwake: false,
    startupVideoLoop: false,
    showMenuTray: true,
    tips: "Use your headset to navigate planet Earth and its wondrous animals. \n\nNo controllers needed. \n\nHold your gaze above and around the planet to zoom to different locations.",
    favorites: [],
    filterFavoritesByDefault: false,
    tutorialMode: "",
    tutorialText:
      "<b>Welcome to WildXR!</b>\n\nUse your headset to navigate planet Earth and its wondrous animals. No controllers needed.\n\n- Hold your gaze above and around the planet to zoom to different locations.\n\n- Hold your gaze on a portal to enter a video gallery. Each portal on the planet will show you different wild and unique animals.\n\n- Hold your gaze at the controls at the bottom of your screen for each view, which can give you more information, play/pause a video, or exit entirely.",
    organizationId: "",
    automaticDownloadModeID: "",
    shouldSendSessionData: false,
  });

  useEffect(() => {
    const loadAllData = async () => {
      try {
        // Fetch installation and catalogues in parallel
        const [
          fetchedAutomaticDownloadModes,
          fetchedInstallation,
          fetchedCatalogues,
          fetchedOrgs,
        ] = await Promise.all([
          getAutomaticDownloadModes(),
          existingInstallation && installationId
            ? getInstallationById(installationId)
            : Promise.resolve(null),
          getVideoCatalogues(),
          getOrganizations(),
        ]);
        setAutomaticDownloadModes(fetchedAutomaticDownloadModes);
        if (fetchedInstallation) {
          setInstallation(fetchedInstallation);
        }
        setVideoCatalogues(fetchedCatalogues);
        setOrganizations(fetchedOrgs);

        // Fetch videos based on the loaded installation's videoCatalogueId
        const fetchedVideos =
          fetchedInstallation && fetchedInstallation.videoCatalogueId
            ? await getVideosByCatalogueId(fetchedInstallation.videoCatalogueId)
            : [];

        setVideos(fetchedVideos); // Ensure fetchedVideos is set correctly

        setPageLoaded(true);
      } catch (err) {
        console.error("Error loading data:", err);
      }
    };

    loadAllData();
  }, [existingInstallation, installationId]);

  const handleSelectInstallation = (installation: Installation) => {
    setInstallation({
      installationId: installation.installationId,
      name: installation.name,
      videoCatalogueId: installation.videoCatalogueId,
      forceOfflineMode: installation.forceOfflineMode,
      showGraphicDeath: installation.showGraphicDeath,
      showGraphicSex: installation.showGraphicSex,
      controls: installation.controls,
      demoMode: installation.demoMode,
      globeStartLat: installation.globeStartLat,
      globeStartLong: installation.globeStartLong,
      appTimerLengthSeconds: installation.appTimerLengthSeconds,
      idleTimerLengthSeconds: installation.idleTimerLengthSeconds,
      idleTimerDelaySeconds: installation.idleTimerDelaySeconds,
      startupVideoId: installation.startupVideoId,
      resumeStartupVideoOnAwake: installation.resumeStartupVideoOnAwake,
      startupVideoLoop: installation.startupVideoLoop,
      showMenuTray: installation.showMenuTray,
      tips: installation.tips,
      favorites: installation.favorites,
      filterFavoritesByDefault: installation.filterFavoritesByDefault,
      tutorialMode: installation.tutorialMode,
      tutorialText: installation.tutorialText,
      organizationId: installation.organizationId,
      automaticDownloadModeID: installation.automaticDownloadModeID,
      shouldSendSessionData: installation.shouldSendSessionData || false,
    });
  };

  const handleChange = (e: any) => {
    const { name, value, checked, type } = e.target;
    const newValue = type === "checkbox" ? checked : value;
    setInstallation((prev: Installation) => ({
      ...prev,
      [name]: newValue,
    }));
  };

  const handleChangeSingleSelect = async (name: string, e: any) => {
    const newValue = e === null ? null : e.value;
    const newLabel = e === null ? null : e.label;

    setInstallation((prevInstallation: any) => ({
      ...prevInstallation,
      [name]: newValue,
      ...(name === "videoCatalogueId" && { startupVideoId: null }), // Clear startupVideoId if videoCatalogueId is changed
    }));

    if (name === "automaticDownloadModeID") {
      setInstallation((prevInstallation: any) => ({
        ...prevInstallation,
        automaticDownloadModeID: newValue,
      }));
    }

    if (name === "environment") {
      setInstallation((prevInstallation: any) => ({
        ...prevInstallation,
        environment: newLabel,
      }));
    }
    if (name === "controls") {
      setInstallation((prevInstallation: any) => ({
        ...prevInstallation,
        controls: newLabel,
      }));
    }
    if (name === "tutorialMode") {
      setInstallation((prevInstallation: any) => ({
        ...prevInstallation,
        tutorialMode: newLabel,
      }));
    }

    if (name === "videoCatalogueId" && newValue) {
      setInstallation((prevInstallation: any) => ({
        ...prevInstallation,
        favorites: [],
        videoCatalogueId: newValue,
      }));
      const newVideos = await getVideosByCatalogueId(newValue);
      setVideos(newVideos); // Assuming setVideos is the function to update the videos state
    }
  };

  const handleSelectFavorites = (selectedOptions: any) => {
    let favoritesArray: any = [];
    // Check if selectedOptions is an array or a single object
    favoritesArray = selectedOptions.map((video: any) => ({
      videoId: video.value,
    }));

    setInstallation((prevInstallation: any) => {
      const updatedInstallation = {
        ...prevInstallation,
        favorites: favoritesArray,
      };
      return updatedInstallation;
    });
  };

  const formIsValid = (): boolean => {
    const newErrors: any = {};
    if (!installation.name) {
      newErrors.name = "Installation name is required";
    }

    if (!installation.automaticDownloadModeID) {
      newErrors.automaticDownloadModeID =
        "Automatic Download Mode selection is required";
    }

    if (currentUser.isSystemAdmin && !installation.organizationId) {
      newErrors.organizationId = "Organization selection is required";
    }

    if (!installation.controls) {
      newErrors.controls = "Controls selection is required";
    }

    if (!installation.videoCatalogueId) {
      newErrors.videoCatalogueId = "Video Catalogue selection is required";
    }
    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async (e: any) => {
    e.preventDefault();
    const updatedInstallation = {
      ...installation,
      installationId: installation.installationId || uuidv4(),
      organizationId:
        installation.organizationId ||
        (currentUser.organizations && currentUser.organizations.length > 0
          ? currentUser.organizations[0].organizationId
          : null),
      favorites: installation.favorites.map((fav) =>
        typeof fav === "string" ? fav : fav.videoId
      ),
      controls: installation.controls?.trim() ? installation.controls : "Gaze",
    };
    try {
      if (!formIsValid()) return;
      if (!existingInstallation) {
        await createInstallation(updatedInstallation);
      } else {
        await updateInstallation(updatedInstallation);
      }
      toast.success(
        installation.name + (existingInstallation ? " Updated" : " Added."),
        {
          position: toast.POSITION.TOP_RIGHT,
          hideProgressBar: true,
          autoClose: 2500,
        }
      );
      navigate("/installations");
      setAddInstallation(false);
      setPageLoaded(false);
    } catch (error) {
      console.log(error);
    }
  };

  const handleCancelEdit = () => {
    setInstallation({
      installationId: "",
      name: "",
      videoCatalogueId: "",
      forceOfflineMode: false,
      showGraphicDeath: false,
      showGraphicSex: false,
      controls: "",
      demoMode: false,
      globeStartLat: 0,
      globeStartLong: -10,
      appTimerLengthSeconds: 0,
      idleTimerLengthSeconds: 0,
      idleTimerDelaySeconds: 0,
      startupVideoId: "",
      resumeStartupVideoOnAwake: false,
      startupVideoLoop: false,
      showMenuTray: false,
      tips: "",
      favorites: [],
      filterFavoritesByDefault: false,
      tutorialMode: "",
      tutorialText: "",
      organizationId: "",
      automaticDownloadModeID: "",
      shouldSendSessionData: false,
    });
    navigate("/installations");
  };

  const handleDelete = async () => {
    try {
      await deleteInstallation(
        installation.installationId ? installation.installationId : ""
      );
      setInstallation({
        installationId: "",
        name: "",
        videoCatalogueId: "",
        forceOfflineMode: false,
        showGraphicDeath: false,
        showGraphicSex: false,
        controls: "Gaze",
        demoMode: false,
        globeStartLat: 0,
        globeStartLong: -10,
        appTimerLengthSeconds: 0,
        idleTimerLengthSeconds: 0,
        idleTimerDelaySeconds: 0,
        startupVideoId: "",
        showMenuTray: false,
        resumeStartupVideoOnAwake: false,
        startupVideoLoop: false,
        tips: "",
        favorites: [],
        filterFavoritesByDefault: false,
        tutorialMode: "",
        tutorialText: "",
        organizationId: "",
        automaticDownloadModeID: "",
        shouldSendSessionData: false,
      });
      toast.success("Installation was deleted.", {
        position: toast.POSITION.TOP_RIGHT,
        hideProgressBar: true,
        autoClose: 2500,
      });
      setPageLoaded(false);
    } catch (error) {
      console.log(error);
    }
  };
  return !pageLoaded ? (
    <div className="flex justify-center items-center">
      <MoonLoader color="#9EB45B" />
    </div>
  ) : (
    <div className="">
      <AddEditInstallation
        installation={installation}
        videoCatalogues={videoCatalogues}
        videos={videos}
        organizations={organizations}
        automaticDownloadModes={automaticDownloadModes}
        errors={errors}
        addInstallation={addInstallation}
        onSelectInstallation={handleSelectInstallation}
        onCancelEdit={handleCancelEdit}
        onChange={handleChange}
        onSave={handleSave}
        onChangeSingleSelect={handleChangeSingleSelect}
        onDelete={handleDelete}
        existingInstallation={existingInstallation}
        onSelectFavorites={handleSelectFavorites}
      />
    </div>
  );
};

export default ManageAddEditInstallation;
